import type { FC, PropsWithChildren } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { FascinationContextValue } from './FascinationContext';
import { FascinationContext } from './FascinationContext';

export const FascinationContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [animate, setAnimate] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const registerRef = useCallback(
    (ref: HTMLElement | null) => {
      if (!ref || animate) {
        return;
      }

      observerRef.current?.disconnect();
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
            setAnimate(true);
            observer.disconnect();
          }
        },
        { threshold: [0.1] },
      );
      observer.observe(ref);
      observerRef.current = observer;
    },
    [animate],
  );

  const value = useMemo<FascinationContextValue>(
    () => ({
      animate,
      registerRef,
      unregisterRef: () => {
        observerRef.current?.disconnect();
      },
    }),
    [animate, registerRef],
  );

  return <FascinationContext.Provider value={value}>{children}</FascinationContext.Provider>;
};
